import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  BaseTransaction,
  ButtonPseudo,
  formatDate,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  TransactionWarnings,
  TransactionTypeEnum,
  PermissionEnum,
  BlacklistDataContent,
  BlacklistUserInfo,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  BlacklistData,
  BlacklistUserInfoData,
  Channel,
  CofProps,
} from '@ui/src/components/transactions-sidepanels/types';
import { clearWarningDetails } from './store/slice';
import {
  useLazyGetWarningDetailsTransactionQuery,
  useLazyGetWarningDetailsEmployeeQuery,
  useLazyGetWarningDetailsClientsOfficiasQuery,
  useLazyGetWarningDetailsChatsQuery,
  useLazyGetWarningByIdQuery,
} from './store/service';

import ROUTES from '../../routes';

import cn from './warning-report.module.css';

const WarningDetails = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const {
    warningsReport: { warningDetails, transactionTypes },
    reportsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);
  const [warningDetailsEmployee, setWarningDetailsEmployee] = useState<
    { id: string; profile: { firstName: string; lastName: string } } | undefined
  >(undefined);
  const [transactionType, setTransactionType] = useState<
    TransactionTypeEnum | undefined
  >(undefined);
  const [blacklistDataVisible, setBlacklistDataVisible] = useState(false);
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [blacklistData, setBlacklistData] = useState<BlacklistData>();
  const [blacklistItem, setBlacklistDataItem] =
    useState<BlacklistUserInfoData>();

  const [companyOfficials, setCompanyOfficials] = useState<
    CofProps[] | undefined
  >(undefined);
  const [chats, setChats] = useState<Channel[] | undefined>(undefined);

  const [warningDetailsTransaction, setWarningDetailsTransaction] = useState<
    BaseTransaction | undefined
  >(undefined);

  const { warningId } = useParams();

  const { search } = useLocation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getWarningDetailsTransaction, { isLoading, isFetching }] =
    useLazyGetWarningDetailsTransactionQuery();
  const [getWarningDetailsEmployee] = useLazyGetWarningDetailsEmployeeQuery();
  const [getCompanyOfficials] = useLazyGetWarningDetailsClientsOfficiasQuery();
  const [getChats] = useLazyGetWarningDetailsChatsQuery();
  const [
    getWarningById,
    { isLoading: warningIsLoading, isFetching: warningIsFetching },
  ] = useLazyGetWarningByIdQuery();

  const handleCloseBlacklistData = () => {
    setBlacklistDataVisible(false);
    setBlacklistItemVisible(false);
    setBlacklistData(undefined);
    setBlacklistDataItem(undefined);
    setPanelIsOpen(true);
  };

  const onWarningEntityClick = async (entity: string) => {
    let link = '#';

    if (entity === 'ORGANIZATION') {
      link = '/customers';

      if (
        warningDetails?.organization?.type === 'INDIVIDUAL' ||
        warningDetails?.organization?.type === 'COMPANY_OFFICIAL'
      ) {
        link = `${link}/individual/${warningDetails?.organizationId}/aml/transactions?ongoingMonitoringItemId=${warningDetails?.warningGroupId}`;
      } else if (warningDetails?.organization?.type === 'CORPORATE') {
        link = `${link}/corporate/${warningDetails?.organizationId}/aml/transactions?ongoingMonitoringItemId=${warningDetails?.warningGroupId}`;
      }
    } else if (entity === 'TRANSACTION') {
      const currentType = transactionTypes?.find(
        (item) => item.value === warningDetailsTransaction?.typeId,
      );

      if (currentType?.name === 'Incoming') {
        link = `/transactions/incoming/${warningDetails?.transactionId}`;
      } else if (currentType?.name === 'Outgoing') {
        link = `/transactions/outgoing/${warningDetails?.transactionId}`;
      } else if (
        currentType?.name === 'Internal' ||
        currentType?.name === 'Internal Payment'
      ) {
        link = `/transactions/internal/${warningDetails?.transactionId}`;
      } else if (currentType?.name === 'Nostro') {
        link = `/transactions/nostro/${warningDetails?.transactionId}`;
      } else if (currentType?.name === 'General Ledger') {
        link = `/transactions/general-ledger/${warningDetails?.transactionId}`;
      }
    } else if (entity === 'APPLICATION') {
      link = '/customers/new-application';

      if (warningDetails?.application?.type === 'INDIVIDUAL') {
        link = `${link}/individual/${warningDetails?.applicationId}`;
      } else if (warningDetails?.application?.type === 'COMPANY_OFFICIAL') {
        link = `${link}/company-official/${warningDetails?.applicationId}`;
      } else if (warningDetails?.application?.type === 'CORPORATE') {
        link = `${link}/corporate/${warningDetails?.applicationId}`;
      }
    } else if (entity === 'PAYMENT_ORDER') {
      link = `/transactions/cash-collection/list/${warningDetails?.warningGroup?.paymentOrderId}`;
    }

    const hiddenElement = document.createElement('a');
    hiddenElement.href = link;
    hiddenElement.target = '_blank';
    hiddenElement.click();
  };

  const getChatsAction = async () => {
    const response = await getChats(warningDetails?.transactionId as string);

    if (response?.data?.data) {
      setChats(response?.data?.data?.edges?.map((item) => item.node));
    }
  };

  const getWarningDetailsTransactionAction = useCallback(
    async (id: string) => {
      const response = await getWarningDetailsTransaction(id);

      if (response?.data?.data) {
        setWarningDetailsTransaction(response?.data?.data);

        const currentType = transactionTypes?.find(
          (item) => item.value === response?.data?.data?.typeId,
        );

        let type = '';

        if (currentType) {
          if (currentType?.name === 'Incoming') {
            type = TransactionTypeEnum.INCOMING;
          } else if (currentType?.name === 'Outgoing') {
            type = TransactionTypeEnum.OUTGOING;
          } else if (currentType?.name === 'Internal') {
            type = TransactionTypeEnum.INTERNAL;
          } else if (currentType?.name === 'Internal Payment') {
            type = TransactionTypeEnum.INTERNAL_PAYMENT;
          } else if (currentType?.name === 'Nostro') {
            type = TransactionTypeEnum.NOSTRO;
          } else if (currentType?.name === 'General Ledger') {
            type = TransactionTypeEnum.GENERAL_LEDGER;
          }

          setTransactionType(type as TransactionTypeEnum);
        }
      }
    },
    [transactionType],
  );

  const getCompanyOfficialsAction = async (id: string) => {
    const response = await getCompanyOfficials(id);

    if (response?.data?.data) {
      setCompanyOfficials(
        response?.data?.data?.edges?.map((item) => item.node),
      );
    }
  };

  const getWarningDetailsEmployeeAction = useCallback(async (id: string) => {
    const response = await getWarningDetailsEmployee(id);

    if (response?.data?.data) {
      setWarningDetailsEmployee(response?.data?.data);
    }
  }, []);

  const handleClose = () => {
    setPanelIsOpen(false);
    setWarningDetailsTransaction(undefined);
    setWarningDetailsEmployee(undefined);
    setTransactionType(undefined);
    setCompanyOfficials(undefined);
    setChats(undefined);
    dispatch(clearWarningDetails());

    setTimeout(() => {
      navigate(`${ROUTES.WARNING_REPORT_LIST}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (warningDetails?.warningEntity === 'TRANSACTION') {
      getWarningDetailsTransactionAction(warningDetails?.transactionId);
    }

    if (warningDetails?.employeeId) {
      getWarningDetailsEmployeeAction(warningDetails?.employeeId);
    }
  }, [warningDetails]);

  useEffect(() => {
    if (warningDetailsTransaction) {
      getChatsAction();

      if (
        warningDetailsTransaction.recipientAccount &&
        warningDetailsTransaction.recipientAccount?.organization?.type ===
          'CORPORATE'
      ) {
        getCompanyOfficialsAction(
          warningDetailsTransaction.recipientAccount.organizationId,
        );
      }
    }
  }, [warningDetailsTransaction]);

  useEffect(() => {
    if (warningId) {
      getWarningById(warningId);
    }
  }, [warningId]);

  let content = <div />;

  if (isLoading || isFetching || !warningDetails) {
    content = <SidePanelLoader />;
  } else if (!isLoading && !isFetching && warningDetails) {
    content = (
      <>
        <div className={cn.warningType}>{warningDetails.warningType}</div>
        <div className={cn.warningEntity}>
          <button
            className={cn.warningEnitityLink}
            type="button"
            onClick={() => onWarningEntityClick(warningDetails?.warningEntity)}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isLoading || isFetching
              ? 'Loading...'
              : warningDetails?.warningEntity === 'ORGANIZATION'
                ? 'Customer'
                : warningDetails?.warningEntity
                    ?.split('_')
                    ?.map(
                      (item) =>
                        `${item.charAt(0)}${item.slice(1).toLowerCase()}`,
                    )
                    .join(' ')}
          </button>
          <div className={cn.warningDate}>
            {formatDate(warningDetails?.createdAt, true)}
          </div>
        </div>
        <div className={cn.warningDetailsSeparator} />
        <div className={cn.warningDetailsInfo}>
          {warningDetails?.organizationId && (
            <div className={cn.warningDetailsInfoRow}>
              <div className={cn.warningDetailsInfoRowTitle}>Customer</div>
              <div className={cn.warningDetailsInfoRowValue}>
                {warningDetails?.organization?.companyName ||
                  `${warningDetails?.organization?.firstName} ${warningDetails?.organization?.lastName}`}
              </div>
            </div>
          )}
          {warningDetails?.applicationId && (
            <div className={cn.warningDetailsInfoRow}>
              <div className={cn.warningDetailsInfoRowTitle}>Application</div>
              <div className={cn.warningDetailsInfoRowValue}>
                {warningDetails?.application?.companyName ||
                  `${warningDetails?.application?.firstName} ${warningDetails?.application?.lastName}`}
              </div>
            </div>
          )}
          {!isLoading &&
            !isFetching &&
            warningDetails?.warningEntity === 'TRANSACTION' && (
              <>
                {warningDetailsTransaction?.senderAccount && (
                  <div className={cn.warningDetailsInfoRow}>
                    <div className={cn.warningDetailsInfoRowTitle}>
                      Sender name
                    </div>
                    <div className={cn.warningDetailsInfoRowValue}>
                      {warningDetailsTransaction?.senderAccount?.description}
                    </div>
                  </div>
                )}
                {(warningDetailsTransaction?.recipientAccount ||
                  warningDetailsTransaction?.beneficiary) && (
                  <div className={cn.warningDetailsInfoRow}>
                    <div className={cn.warningDetailsInfoRowTitle}>
                      Beneficiary name
                    </div>
                    <div className={cn.warningDetailsInfoRowValue}>
                      {warningDetailsTransaction?.recipientAccount
                        ?.description ||
                        warningDetailsTransaction?.beneficiary?.name}
                    </div>
                  </div>
                )}
              </>
            )}
        </div>
        {((!isLoading && !isFetching) || warningDetails?.organizationId) && (
          <div className={cn.warningDetailsSeparator} />
        )}
        <TransactionWarnings
          warnings={[warningDetails]}
          employee={warningDetailsEmployee}
          transactionType={transactionType}
          transactionsTypes={
            transactionTypes?.map((item) => ({
              id: item.value,
              name: item.name,
            })) as { name: string; id: string }[]
          }
          setBlacklistDataVisible={(visible) => {
            setBlacklistDataVisible(visible);
            setPanelIsOpen(false);
          }}
          setBlacklistItemVisible={(visible) => {
            setBlacklistItemVisible(visible);
            setPanelIsOpen(false);
          }}
          setBlacklistData={setBlacklistData}
          setBlacklistDataItem={setBlacklistDataItem}
          applyTransactionsWarningIsLoading={false}
          companyOfficials={companyOfficials}
          individualCustomer={warningDetailsTransaction?.recipientAccount}
          chats={chats}
          activePermissions={activePermissions}
          hiddenApprove={
            !activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION,
            )
          }
          hiddenReject={
            !activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_INCOMING_AML_REJECT_ACTION,
            )
          }
          getWarningById={() => getWarningById(warningDetails?.id)}
          isWarningByIdFetching={warningIsFetching}
          isWarningByIdLoading={warningIsLoading}
          warningsReports
        />
      </>
    );
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        closeOnClickOutside
        onClose={handleClose}
        title="Warning details"
        sidepanelExpandedKey="warning-details-panel"
        type={SidePanelType.Transactions}
        footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
      >
        {content}
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        className={cn.transactionSidepanel}
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="incoming-blacklist-data"
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisible}
        onClose={handleCloseBlacklistData}
        title="User Details"
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        className={cn.transactionSidepanel}
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="incoming-blacklist-data"
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default WarningDetails;
